/**
 * 城市合伙人管理页。
 *
 * 功能：
 *  - 列表：状态 / 城市 / 归因用户数 / 累计成交 / **待结算佣金**
 *  - 建档：填姓名等 → 系统发推广口令 + 一次性合作码（合作码要你在微信里发给对方）
 *  - 停止合作：推广码立即失效，历史归因与未结佣金一律保留
 *  - 生成小程序码：服务端调微信 getUnlimited，scene = p=<推广口令>
 *  - 人工补录归因：按 userId 把用户挂到合伙人名下（归因漏斗的兜底层）
 *  - 详情抽屉：归因用户明细（含未成交）+ 佣金流水（含成交用户与作品名）
 *
 * 管理员看得到真实用户身份，合伙人端只看得到脱敏标签（server 侧 maskUser），
 * 两条链路的数据源刻意分开，勿合并。
 *
 * 权限（原先一个点位捆了全部写动作，现按动作拆三个，后端同样分别校验）：
 *  - partner:read      — 全页只读
 *  - partner:create    — 新建合伙人 / 生成小程序码（含看到合作码明文）
 *  - partner:stop      — 停止合作
 *  - partner:attribute — 人工补录归因（碰钱，仅超管）
 */
import { useState } from 'react';
import {
  Card, Table, Form, Input, Button, Space, Tag, Modal, Drawer,
  Alert, message, Select, Typography, Descriptions, Popconfirm, Tabs, Checkbox,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PermissionGate } from '../../components/PermissionGate';
import { PERMISSIONS } from '../../contracts/permissions';
import {
  listPartners, createPartner, stopPartner, issuePartnerQrcode,
  attributeManually, getPartnerDetail, listPartnerAttributions,
  type PartnerSummaryDto, type PartnerStatus, type CommissionStatus,
  type PartnerAttributionDto, type CommissionEntryDto,
} from '../../api/partners';

const { Text, Paragraph } = Typography;

function formatTs(ms: number | null): string {
  if (ms == null) return '-';
  return new Date(ms).toLocaleString('zh-CN', { hour12: false });
}

/** 分 → 元，保留两位（佣金固定 300 元，但加印等场景可能出现非整元） */
function yuan(fen: number): string {
  return `¥${(fen / 100).toFixed(2)}`;
}

const STATUS_META: Record<PartnerStatus, { color: string; label: string }> = {
  pending_bind: { color: 'orange', label: '待认领' },
  active: { color: 'green', label: '合作中' },
  stopped: { color: 'default', label: '已停止' },
};

const COMMISSION_STATUS_META: Record<CommissionStatus, { color: string; label: string }> = {
  pending: { color: 'orange', label: '待生效' },
  payable: { color: 'blue', label: '可结算' },
  settled: { color: 'green', label: '已打款' },
  void: { color: 'default', label: '已作废' },
};

const ATTRIBUTION_SOURCE_LABELS: Record<PartnerAttributionDto['source'], string> = {
  qrcode: '扫码',
  promo_code: '推广口令',
  manual: '人工补录',
};

/**
 * 用户身份单元格：主行昵称、次行手机号 / userId（便于直接复制去「用户查询」页）。
 * 扫码后从未注册的访客没有任何账号字段，此时只能显示归因哈希。
 */
function UserCell(props: {
  displayName: string | null;
  phone: string | null;
  userId: string | null;
  fallbackHash?: string;
}) {
  const { displayName, phone, userId, fallbackHash } = props;
  if (!userId && !displayName) {
    return (
      <Space direction="vertical" size={0}>
        <Text type="secondary">未注册访客</Text>
        {fallbackHash && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {fallbackHash.slice(0, 12)}…
          </Text>
        )}
      </Space>
    );
  }
  return (
    <Space direction="vertical" size={0}>
      <Text strong>{displayName || '未设昵称'}</Text>
      <Text type="secondary" style={{ fontSize: 12 }} copyable={userId ? { text: userId } : false}>
        {phone || userId}
      </Text>
    </Space>
  );
}

export default function PartnersPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<PartnerStatus | undefined>();
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [attributeTarget, setAttributeTarget] = useState<PartnerSummaryDto | null>(null);
  const [createForm] = Form.useForm();
  const [attributeForm] = Form.useForm();

  const { data: partners = [], isLoading } = useQuery({
    queryKey: ['partners', statusFilter],
    queryFn: () => listPartners({ status: statusFilter }),
  });

  const { data: detail } = useQuery({
    queryKey: ['partner-detail', detailId],
    queryFn: () => getPartnerDetail(detailId as string),
    enabled: !!detailId,
  });

  const { data: attributions = [], isLoading: attributionsLoading } = useQuery({
    queryKey: ['partner-attributions', detailId],
    queryFn: () => listPartnerAttributions(detailId as string),
    enabled: !!detailId,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['partners'] });
    if (detailId) {
      qc.invalidateQueries({ queryKey: ['partner-detail', detailId] });
      qc.invalidateQueries({ queryKey: ['partner-attributions', detailId] });
    }
  };

  const createMutation = useMutation({
    mutationFn: createPartner,
    onSuccess: (partner) => {
      setCreateOpen(false);
      createForm.resetFields();
      refresh();
      // 合作码是一次性的，建档后必须让运营立刻看到并抄走
      Modal.success({
        title: '建档成功',
        width: 520,
        content: (
          <div>
            <Paragraph>
              请把下面的<Text strong>合作码</Text>在微信里发给对方，对方在小程序输入后
              即完成身份绑定（合作码一次性，用过即失效）。
            </Paragraph>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="姓名">{partner.name}</Descriptions.Item>
              <Descriptions.Item label="推广口令">
                <Text copyable strong>{partner.promoCode}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="合作码">
                <Text copyable strong>{partner.activationCode}</Text>
              </Descriptions.Item>
            </Descriptions>
          </div>
        ),
      });
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message ?? '建档失败');
    },
  });

  const stopMutation = useMutation({
    mutationFn: stopPartner,
    onSuccess: () => {
      message.success('已停止合作（历史归因与未结佣金保留）');
      refresh();
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message ?? '操作失败');
    },
  });

  const qrcodeMutation = useMutation({
    mutationFn: issuePartnerQrcode,
    onSuccess: (data) => {
      refresh();
      Modal.success({
        title: '小程序码已生成',
        content: (
          <div>
            <Paragraph>把这张码发给合伙人，用户扫码后业绩自动归属于他。</Paragraph>
            <img src={data.qrcodeUrl} alt="小程序码" style={{ width: 240 }} />
            <Paragraph copyable={{ text: data.qrcodeUrl }}>{data.qrcodeUrl}</Paragraph>
          </div>
        ),
      });
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message ?? '生成失败');
    },
  });

  const attributeMutation = useMutation({
    mutationFn: (v: { partnerId: string; userId: string; force?: boolean }) =>
      attributeManually(v.partnerId, v.userId, v.force),
    onSuccess: (data) => {
      setAttributeTarget(null);
      attributeForm.resetFields();
      refresh();
      if (data.bound) {
        // 强制改归属时把"从谁改到谁"回显出来，让运营当场能核对自己没点错
        message.success(
          data.previousPartnerName
            ? `已改归属：原归属「${data.previousPartnerName}」→ 本合伙人`
            : '补录成功',
        );
      } else if (data.alreadyBound) {
        // first-touch 不覆盖：这是规则而非失败，要如实告知运营，
        // 并点名现归属方 —— 他下一步就是决定要不要勾「强制改归属」
        message.warning(
          data.previousPartnerName
            ? `该用户已归属「${data.previousPartnerName}」，按先到先得规则未覆盖；确需纠正请勾选「强制改归属」`
            : '该用户此前已被其他合伙人绑定，按先到先得规则未覆盖',
        );
      }
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message ?? '补录失败');
    },
  });

  const columns: ColumnsType<PartnerSummaryDto> = [
    { title: '姓名', dataIndex: 'name', key: 'name' },
    { title: '城市', dataIndex: 'city', key: 'city', render: (v) => v || '-' },
    { title: '微信号', dataIndex: 'wechatId', key: 'wechatId', render: (v) => v || '-' },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (s: PartnerStatus) => (
        <Tag color={STATUS_META[s].color}>{STATUS_META[s].label}</Tag>
      ),
    },
    {
      title: '推广口令',
      dataIndex: 'promoCode',
      key: 'promoCode',
      render: (v: string) => <Text copyable>{v}</Text>,
    },
    { title: '归因用户', dataIndex: 'boundUserCount', key: 'boundUserCount' },
    { title: '成交', dataIndex: 'convertedUserCount', key: 'convertedUserCount' },
    {
      title: '待结算佣金',
      dataIndex: 'payableFen',
      key: 'payableFen',
      render: (fen: number, row) => (
        <Text strong>{yuan(fen)}{row.payableCount ? ` (${row.payableCount} 单)` : ''}</Text>
      ),
    },
    {
      title: '待生效',
      dataIndex: 'pendingFen',
      key: 'pendingFen',
      render: (fen: number) => yuan(fen),
    },
    { title: '建档时间', dataIndex: 'createdAt', key: 'createdAt', render: formatTs },
    {
      title: '操作',
      key: 'action',
      render: (_, row) => (
        <Space size="small" wrap>
          <Button size="small" onClick={() => setDetailId(row.partnerId)}>详情</Button>
          <PermissionGate point={PERMISSIONS.PARTNER_CREATE}>
            <Button
              size="small"
              onClick={() => qrcodeMutation.mutate(row.partnerId)}
              loading={qrcodeMutation.isPending}
            >
              小程序码
            </Button>
          </PermissionGate>
          <PermissionGate point={PERMISSIONS.PARTNER_ATTRIBUTE}>
            <Button size="small" onClick={() => setAttributeTarget(row)}>补录归因</Button>
          </PermissionGate>
          <PermissionGate point={PERMISSIONS.PARTNER_STOP}>
            {row.status !== 'stopped' && (
              <Popconfirm
                title="停止合作？"
                description="推广码立即失效，历史归因与未结佣金保留。"
                onConfirm={() => stopMutation.mutate(row.partnerId)}
              >
                <Button size="small" danger>停止</Button>
              </Popconfirm>
            )}
          </PermissionGate>
        </Space>
      ),
    },
  ];

  return (
    <PermissionGate point={PERMISSIONS.PARTNER_READ}>
      <Card
        title="城市合伙人"
        extra={
          <Space>
            <Select
              allowClear
              placeholder="状态筛选"
              style={{ width: 140 }}
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: 'pending_bind', label: '待认领' },
                { value: 'active', label: '合作中' },
                { value: 'stopped', label: '已停止' },
              ]}
            />
            <PermissionGate point={PERMISSIONS.PARTNER_CREATE}>
              <Button type="primary" onClick={() => setCreateOpen(true)}>新建合伙人</Button>
            </PermissionGate>
          </Space>
        }
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="佣金结算口径"
          description="用户付款后佣金记为「待生效」；用户定稿并确认制作（进入印刷、不再支持退款）后转「可结算」。每月 10 日、25 日两次人工打款。"
        />
        <Table
          rowKey="partnerId"
          loading={isLoading}
          columns={columns}
          dataSource={partners}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 1200 }}
        />
      </Card>

      <Modal
        title="新建合伙人"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => createForm.submit()}
        confirmLoading={createMutation.isPending}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="线下谈成后再建档。建档后系统会发一个一次性合作码，需你在微信里发给对方。"
        />
        <Form
          form={createForm}
          layout="vertical"
          onFinish={(v) => createMutation.mutate(v)}
        >
          <Form.Item name="name" label="姓名 / 机构名" rules={[{ required: true }]}>
            <Input placeholder="如：王老师 / 灵感书店" maxLength={64} />
          </Form.Item>
          <Form.Item name="wechatId" label="微信号">
            <Input placeholder="用于结算联系" maxLength={64} />
          </Form.Item>
          <Form.Item name="phone" label="手机号">
            <Input maxLength={32} />
          </Form.Item>
          <Form.Item name="city" label="城市">
            <Input placeholder="如：杭州" maxLength={64} />
          </Form.Item>
          <Form.Item name="note" label="备注">
            <Input.TextArea rows={2} maxLength={255} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`人工补录归因 — ${attributeTarget?.name ?? ''}`}
        open={!!attributeTarget}
        onCancel={() => setAttributeTarget(null)}
        onOk={() => attributeForm.submit()}
        confirmLoading={attributeMutation.isPending}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="按先到先得（first-touch）规则：该用户若已被其他合伙人绑定，本次不会覆盖；归属已过期（180 天）的会自动接手。"
        />
        <Form
          form={attributeForm}
          layout="vertical"
          onFinish={(v) =>
            attributeTarget &&
            attributeMutation.mutate({
              partnerId: attributeTarget.partnerId,
              userId: v.userId,
              force: !!v.force,
            })
          }
        >
          <Form.Item name="userId" label="用户 ID" rules={[{ required: true }]}>
            <Input placeholder="在「用户查询」页可查到 userId" />
          </Form.Item>
          {/*
            强制改归属是纠错通道：归因一旦写错（用户报错了暗号、两个合伙人抢同一个
            客户），在 first-touch + 唯一键的约束下系统里本来是改不动的。
            默认不勾，勾了会留审计（记下从谁改到谁）。
          */}
          <Form.Item name="force" valuePropName="checked" style={{ marginBottom: 0 }}>
            <Checkbox>强制改归属（覆盖现有归属，操作留审计）</Checkbox>
          </Form.Item>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            仅用于纠正写错的归属。已产生的佣金流水不受影响，改归属只影响此后的成交。
          </Typography.Text>
        </Form>
      </Modal>

      <Drawer
        title={`合伙人详情 — ${detail?.partner.name ?? ''}`}
        open={!!detailId}
        onClose={() => setDetailId(null)}
        width={880}
      >
        {detail && (
          <>
            <Descriptions column={2} bordered size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="状态">
                <Tag color={STATUS_META[detail.partner.status].color}>
                  {STATUS_META[detail.partner.status].label}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="城市">{detail.partner.city || '-'}</Descriptions.Item>
              <Descriptions.Item label="微信号">{detail.partner.wechatId || '-'}</Descriptions.Item>
              <Descriptions.Item label="手机号">{detail.partner.phone || '-'}</Descriptions.Item>
              <Descriptions.Item label="推广口令">
                <Text copyable>{detail.partner.promoCode}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="合作码">
                {detail.partner.activationCode ? (
                  <Text copyable>{detail.partner.activationCode}</Text>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="归因用户">{detail.partner.boundUserCount}</Descriptions.Item>
              <Descriptions.Item label="成交用户">
                {detail.partner.convertedUserCount}
              </Descriptions.Item>
              <Descriptions.Item label="待结算">
                <Text strong>{yuan(detail.partner.payableFen)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="已打款">
                {yuan(detail.partner.settledFen)}
              </Descriptions.Item>
            </Descriptions>

            <Tabs
              defaultActiveKey="attributions"
              items={[
                {
                  key: 'attributions',
                  label: `归因用户 (${detail.partner.boundUserCount})`,
                  children: (
                    <>
                      <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 12 }}
                        message="扫码当时用户往往还没注册，归因按微信身份记录。显示「未注册访客」= 该身份至今未创建账号，成交后会自动补上身份。"
                      />
                      <Table
                        rowKey="attributionId"
                        size="small"
                        loading={attributionsLoading}
                        dataSource={attributions}
                        pagination={{ pageSize: 20 }}
                        columns={[
                          {
                            title: '用户',
                            key: 'user',
                            render: (_, row: PartnerAttributionDto) => (
                              <UserCell
                                displayName={row.displayName}
                                phone={row.phone}
                                userId={row.userId}
                                fallbackHash={row.identityHash}
                              />
                            ),
                          },
                          {
                            title: '状态',
                            key: 'state',
                            render: (_, row: PartnerAttributionDto) => {
                              if (row.convertedAt) return <Tag color="green">已成交</Tag>;
                              // 90 天未成交即失效，过期归因不再产生佣金，要和"待成交"区分开
                              if (row.expireAt < Date.now()) return <Tag>已过期</Tag>;
                              return <Tag color="blue">待成交</Tag>;
                            },
                          },
                          {
                            title: '来源',
                            dataIndex: 'source',
                            key: 'source',
                            render: (s: PartnerAttributionDto['source']) =>
                              ATTRIBUTION_SOURCE_LABELS[s] ?? s,
                          },
                          {
                            title: '归因时间',
                            dataIndex: 'boundAt',
                            key: 'boundAt',
                            render: formatTs,
                          },
                          {
                            title: '成交时间',
                            dataIndex: 'convertedAt',
                            key: 'convertedAt',
                            render: formatTs,
                          },
                          {
                            title: '归因失效',
                            dataIndex: 'expireAt',
                            key: 'expireAt',
                            render: formatTs,
                          },
                        ]}
                      />
                    </>
                  ),
                },
                {
                  key: 'commissions',
                  label: `佣金流水 (${detail.commissions.length})`,
                  children: (
                    <Table
                      rowKey="entryId"
                      size="small"
                      dataSource={detail.commissions}
                      pagination={{ pageSize: 20 }}
                      scroll={{ x: 900 }}
                      columns={[
                        {
                          title: '状态',
                          dataIndex: 'status',
                          key: 'status',
                          render: (s: CommissionStatus) => (
                            <Tag color={COMMISSION_STATUS_META[s].color}>
                              {COMMISSION_STATUS_META[s].label}
                            </Tag>
                          ),
                        },
                        {
                          title: '成交用户',
                          key: 'user',
                          render: (_, row: CommissionEntryDto) => (
                            <UserCell
                              displayName={row.userDisplayName}
                              phone={row.userPhone}
                              userId={row.userId}
                            />
                          ),
                        },
                        {
                          title: '作品',
                          key: 'work',
                          render: (_, row: CommissionEntryDto) => (
                            <Space direction="vertical" size={0}>
                              <Text>{row.workTitle || '（作品已删除）'}</Text>
                              <Text
                                type="secondary"
                                style={{ fontSize: 12 }}
                                copyable={{ text: row.workId }}
                              >
                                {row.workId}
                              </Text>
                            </Space>
                          ),
                        },
                        {
                          title: '佣金',
                          dataIndex: 'commissionFen',
                          key: 'commissionFen',
                          render: yuan,
                        },
                        {
                          title: '订单金额',
                          dataIndex: 'orderAmountFen',
                          key: 'orderAmountFen',
                          render: yuan,
                        },
                        { title: '付款时间', dataIndex: 'pendingAt', key: 'pendingAt', render: formatTs },
                        {
                          title: '转可结算',
                          dataIndex: 'payableAt',
                          key: 'payableAt',
                          render: formatTs,
                        },
                        {
                          title: '策略版本',
                          dataIndex: 'policyVersion',
                          key: 'policyVersion',
                        },
                      ]}
                    />
                  ),
                },
              ]}
            />
          </>
        )}
      </Drawer>
    </PermissionGate>
  );
}
